import {
  type ChecklistCode,
  ChecklistPosition,
  diagnosisIdFor,
  fixKeyFor,
  labelKeyFor,
  severityOf,
} from "./checklist-position";

/**
 * Gateway-only readiness signals. Present on the hosted-web path (which enrolls
 * through a gateway session); omitted on the on-prem Bridge path, where these
 * checks do not apply.
 *
 * @since 0.0.0
 */
export interface GatewayChecklistSignals {
  readonly hasSession: boolean;
  readonly enrolled: boolean;
  readonly ready: boolean;
  readonly expired: boolean;
  readonly tokenVerificationFailed: boolean;
}

/**
 * Platform-agnostic inputs for {@link derivePreflightChecklist}. The core
 * signals apply to every shell and the Bridge; `gateway` is supplied only by
 * the hosted-web adapter.
 *
 * @since 0.0.0
 */
export interface PreflightChecklistInputs {
  readonly generalApiReachable: boolean;
  readonly telematikAvailable: boolean;
  readonly selectedCardReaderAvailable: boolean;
  readonly hasMedicalCard: boolean;
  readonly practiceName: string | undefined;
  readonly deviceName: string | undefined;
  readonly serverTokenConfigured: boolean;
  readonly insuiteRestUrl: string | undefined;
  readonly revoked: boolean;
  readonly gateway?: GatewayChecklistSignals;
}

type CheckOutcome = "ok" | "failed" | "skip";

interface CheckDefinition {
  readonly code: ChecklistCode;
  readonly evaluate: (inputs: PreflightChecklistInputs) => CheckOutcome;
}

const has = (value: string | undefined): boolean => Boolean(value?.trim());

/**
 * Ordered check definitions. Order is fixed so derivation is deterministic and
 * every surface renders positions in the same sequence. `skip` outcomes are
 * omitted from the result (e.g. gateway checks on the on-prem Bridge path, or
 * fault-only checks when no fault is present).
 */
const CHECKS: readonly CheckDefinition[] = [
  {
    code: "general_api_unavailable",
    evaluate: (i) => (i.generalApiReachable ? "ok" : "failed"),
  },
  {
    code: "missing_insuite_url",
    evaluate: (i) => (has(i.insuiteRestUrl) ? "ok" : "failed"),
  },
  {
    code: "missing_server_token",
    evaluate: (i) => (i.serverTokenConfigured ? "ok" : "failed"),
  },
  {
    code: "missing_gateway_session",
    evaluate: (i) =>
      i.gateway === undefined ? "skip" : i.gateway.hasSession ? "ok" : "failed",
  },
  {
    code: "missing_gateway_enrollment",
    evaluate: (i) =>
      i.gateway === undefined ? "skip" : i.gateway.enrolled ? "ok" : "failed",
  },
  {
    code: "gateway_not_ready",
    evaluate: (i) =>
      i.gateway === undefined ? "skip" : i.gateway.ready ? "ok" : "failed",
  },
  {
    code: "token_verification_failed",
    evaluate: (i) =>
      i.gateway?.tokenVerificationFailed ? "failed" : "skip",
  },
  {
    code: "session_expired",
    evaluate: (i) => (i.gateway?.expired ? "failed" : "skip"),
  },
  {
    code: "device_revoked",
    evaluate: (i) => (i.revoked ? "failed" : "skip"),
  },
  {
    code: "missing_practice_name",
    evaluate: (i) => (has(i.practiceName) ? "ok" : "failed"),
  },
  {
    code: "missing_device_name",
    evaluate: (i) => (has(i.deviceName) ? "ok" : "failed"),
  },
  {
    code: "telematik_unavailable",
    evaluate: (i) => (i.telematikAvailable ? "ok" : "failed"),
  },
  {
    code: "selected_card_reader_unavailable",
    evaluate: (i) => (i.selectedCardReaderAvailable ? "ok" : "failed"),
  },
  {
    code: "medical_card_unavailable",
    evaluate: (i) => (i.hasMedicalCard ? "ok" : "failed"),
  },
];

/**
 * Derives the ordered preflight checklist from readiness inputs. Total and
 * deterministic: identical inputs always yield the same ordered positions.
 * Warnings (Telematik / card reader / medical card) are included but never
 * block readiness.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * const positions = derivePreflightChecklist({
 *   generalApiReachable: true,
 *   telematikAvailable: true,
 *   selectedCardReaderAvailable: true,
 *   hasMedicalCard: true,
 *   practiceName: "Praxis Weber",
 *   deviceName: "Empfang 1",
 *   serverTokenConfigured: true,
 *   insuiteRestUrl: "https://weber.hub.doc-cirrus.com/.../_rest_",
 *   revoked: false,
 * })
 * ```
 */
export function derivePreflightChecklist(
  inputs: PreflightChecklistInputs,
): readonly ChecklistPosition[] {
  const positions: ChecklistPosition[] = [];
  for (const check of CHECKS) {
    const outcome = check.evaluate(inputs);
    if (outcome === "skip") continue;
    const severity = severityOf(check.code);
    positions.push(
      new ChecklistPosition({
        code: check.code,
        severity,
        state: outcome,
        label: labelKeyFor(check.code),
        fix: fixKeyFor(check.code),
        diagnosisId: outcome === "failed" ? diagnosisIdFor(check.code) : undefined,
      }),
    );
  }
  return positions;
}

/**
 * A device is ready when no blocker position is failed. Failed warnings do not
 * affect readiness.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * isReady(derivePreflightChecklist(inputs))
 * ```
 */
export function isReady(positions: readonly ChecklistPosition[]): boolean {
  return !positions.some(
    (position) => position.severity === "blocker" && position.state === "failed",
  );
}

/**
 * True when every position is `ok` — the state that collapses the checklist to
 * a single green line. A present warning keeps this false while readiness stays
 * true.
 *
 * @since 0.0.0
 * @example
 * ```ts
 * collapsesToGreen(derivePreflightChecklist(inputs))
 * ```
 */
export function collapsesToGreen(
  positions: readonly ChecklistPosition[],
): boolean {
  return positions.every((position) => position.state === "ok");
}
